import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Search as SearchIcon } from "lucide-react";
import { Seo, Reveal } from "../components/Seo";
import { searchSite } from "../lib/site";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const results = q ? searchSite(q) : [];

  return (
    <>
      <Seo
        title={`Search — Softline Nigeria`}
        description="Search products, services, industries and insights across the Softline Nigeria website."
        path="/search"
      />
      <section className="bg-charcoal noise-bg pt-36 pb-16 md:pt-44 md:pb-20" data-testid="search-page">
        <div className="shell">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">Search</p>
          <h1 className="font-display font-bold tracking-tight text-white text-4xl md:text-5xl mt-5">
            {q ? `Results for “${q}”` : "Search Softline Nigeria"}
          </h1>
          <form
            className="mt-9 flex max-w-xl"
            data-testid="site-search-form"
            onSubmit={(e) => {
              e.preventDefault();
              setParams(input.trim() ? { q: input.trim() } : {});
            }}
          >
            <label htmlFor="site-search" className="sr-only">Search the site</label>
            <div className="relative flex-1">
              <SearchIcon size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" aria-hidden="true" />
              <input
                id="site-search"
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Products, services, industries, insights…"
                data-testid="site-search-input"
                className="w-full bg-white border border-charcoal/20 pl-11 pr-4 py-3.5 text-[15px] text-charcoal focus:outline-none focus:ring-2 focus:ring-lime"
              />
            </div>
            <button type="submit" data-testid="site-search-submit" className="bg-lime text-charcoal font-semibold px-6 hover:bg-limelight transition-colors duration-200">
              Search
            </button>
          </form>
        </div>
      </section>

      <section className="bg-white" data-testid="search-results">
        <div className="shell py-20">
          {q ? (
            results.length ? (
              <ul className="divide-y divide-charcoal/10 border-y border-charcoal/10">
                {results.map((r, i) => (
                  <Reveal key={r.path + r.title} delay={i * 0.04}>
                    <li>
                      <Link
                        to={r.path}
                        data-testid={`search-result-${i}`}
                        className="group flex items-center justify-between gap-6 py-6 px-2 hover:bg-surface transition-colors duration-150"
                      >
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/45">{r.path}</p>
                          <h2 className="font-display font-bold text-xl mt-1.5">{r.title}</h2>
                          <p className="text-sm text-charcoal/60 mt-1.5 max-w-2xl">{r.excerpt}</p>
                        </div>
                        <ArrowUpRight size={20} className="text-charcoal/30 group-hover:text-lime shrink-0 transition-colors duration-200" />
                      </Link>
                    </li>
                  </Reveal>
                ))}
              </ul>
            ) : (
              <div className="border border-charcoal/15 p-10 max-w-2xl" data-testid="search-empty-state">
                <p className="font-display font-bold text-xl">No results for “{q}”.</p>
                <p className="text-sm text-charcoal/60 mt-2">
                  Check the spelling, try a broader term, or browse the <Link to="/products" className="font-semibold underline">products</Link> and{" "}
                  <Link to="/services" className="font-semibold underline">services</Link> directly.
                </p>
              </div>
            )
          ) : (
            <p className="text-charcoal/60" data-testid="search-hint">
              Enter a term above — for example “mailbox”, “identity”, “cloud” or “government”.
            </p>
          )}
        </div>
      </section>
    </>
  );
}
